import React, { useContext, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Button, Tooltip } from "@mui/material";
import {
  CloudProviderContext,
  getAccountCloudType,
  shouldShowAzurePlaceholderView,
} from "../../CloudProviderContext";
import getUserRole from "../../helpers/jwt-token-access/userRole";
import { requestOpenPairAccount } from "../../helpers/pairAccountNavigation";

const ALLOWED_ROLES = ["Administrator"];

/**
 * Idea A: pinned sidebar footer CTA for Pair Account / Pair Subscription.
 * Does not modify the Configuration page Pair button.
 */
const SidebarPairAccountCta = ({ isCollapsed = false }) => {
  const history = useHistory();
  const location = useLocation();
  const { selectedProvider } = useContext(CloudProviderContext);
  const licensing = useSelector((state) => state.licensing);
  const acctList = useSelector((state) => state.acctList);
  const profile = useSelector((state) => state.profile);
  const userRole = getUserRole();

  const isAzureView = useMemo(
    () => shouldShowAzurePlaceholderView(selectedProvider),
    [selectedProvider]
  );

  const pairLabel = isAzureView ? "Pair Subscription" : "Pair Account";

  const showPairButton =
    licensing?.data?.licenseName !== "AwsMarketPlaceLicense";

  const roleAccess = useMemo(() => {
    if (
      licensing?.data?.isLicensingComplete &&
      profile?.userRoles?.includes("Administrator")
    ) {
      return 0;
    }
    return -1;
  }, [licensing, profile]);

  const providerAccounts = useMemo(() => {
    const accounts = acctList?.accounts || [];
    return accounts.filter((account) => {
      if (account.accountNumber?.includes("INIT00000001")) return false;
      return getAccountCloudType(account) === selectedProvider;
    });
  }, [acctList?.accounts, selectedProvider]);

  const pairedCount = providerAccounts.filter(
    (x) =>
      !x.accountNumber?.includes("INIT00000001") &&
      !x.accountNumber?.includes("TEST") &&
      x.accountNumber?.length === 12
  ).length;

  const numAccounts = licensing?.data?.numAccounts;
  const pairDisabled =
    roleAccess < 0 ||
    (typeof numAccounts === "number" && pairedCount >= numAccounts) ||
    !ALLOWED_ROLES.includes(userRole) ||
    licensing?.data?.licenseName === "CUSTOMER";

  if (!showPairButton) return null;

  const handleClick = () => {
    if (pairDisabled) return;
    requestOpenPairAccount(history, location);
  };

  if (isCollapsed) {
    return (
      <Box className="sidebar-pair-account-cta sidebar-pair-account-cta--collapsed">
        <Tooltip title={pairLabel} placement="right">
          <span>
            <Button
              onClick={handleClick}
              disabled={pairDisabled}
              aria-label={pairLabel}
              className="sidebar-pair-account-btn sidebar-pair-account-btn--icon"
            >
              <i className="mdi mdi-plus" aria-hidden="true" />
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box className="sidebar-pair-account-cta">
      <Button
        onClick={handleClick}
        disabled={pairDisabled}
        startIcon={<i className="mdi mdi-plus" aria-hidden="true" />}
        className="sidebar-pair-account-btn"
        fullWidth
      >
        {pairLabel}
      </Button>
    </Box>
  );
};

export default SidebarPairAccountCta;
